import React, { forwardRef, useMemo, useState } from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { ChevronDownIcon, ChevronUpIcon } from 'assets/icons';
import classNames from 'classnames';
import { TPillSelectOptionBase } from 'types/ui';

import { Pill } from '../Pill';
import { SelectItem } from './SelectItem';

import styles from './PillSelect.module.scss';

export interface IPillSelectProps extends RadixSelect.SelectProps {
    options: TPillSelectOptionBase[];
    placeholder?: string;
}

export const PillSelect = forwardRef<HTMLButtonElement, IPillSelectProps>(
    ({ options, value: controlledValue, onValueChange: controlledOnValueChange, placeholder, ...rootProps }, ref) => {
        const [value, setValue] = useState<string | undefined>(undefined);

        const selectedOption = useMemo(() => {
            const valueToCheck = controlledValue || value;
            return options.find((option) => option.value === valueToCheck);
        }, [options, value, controlledValue]);

        return (
            <RadixSelect.Root
                value={controlledValue || value}
                onValueChange={controlledOnValueChange || setValue}
                {...rootProps}
            >
                <RadixSelect.Trigger ref={ref} asChild>
                    <Pill
                        prefix={selectedOption?.icon}
                        suffix={
                            <RadixSelect.Icon>
                                <ChevronDownIcon className={styles.triggerIcon} />
                            </RadixSelect.Icon>
                        }
                        className={classNames(styles.triggerButton)}
                        style={{
                            backgroundColor: selectedOption?.backgroundColor,
                            color: selectedOption?.color,
                        }}
                    >
                        <RadixSelect.Value placeholder={placeholder} />
                    </Pill>
                </RadixSelect.Trigger>

                <RadixSelect.Portal>
                    <RadixSelect.Content className={styles.content} position="item-aligned">
                        <RadixSelect.ScrollUpButton className={styles.scrollButtonUp}>
                            <ChevronUpIcon />
                        </RadixSelect.ScrollUpButton>
                        <RadixSelect.Viewport className={styles.viewport}>
                            {options.map((option) => (
                                <SelectItem key={value} value={option.value} option={option}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </RadixSelect.Viewport>
                        <RadixSelect.ScrollDownButton className={styles.scrollButtonDown}>
                            <ChevronDownIcon />
                        </RadixSelect.ScrollDownButton>
                        <RadixSelect.Arrow />
                    </RadixSelect.Content>
                </RadixSelect.Portal>
            </RadixSelect.Root>
        );
    },
);
